import { z } from "zod";
import { producerStandingSchema } from "./producer.js";
/**
 * Everything the registration form offers, in one response.
 *
 * One endpoint rather than six, because six would be six round trips to
 * Frankfurt to fill one form, and because the lists cascade: a region belongs
 * to a country and a shelf to a storage, so the client can narrow what it
 * offers without asking again. The cascade is data, not a request.
 *
 * The lists are drawn from every wine the cellar has ever held, never from
 * what is in stock. Derived from bottles on the shelf, drinking the last
 * Bollinger would remove Bollinger from the producer list and the next bottle
 * of it would be retyped and misspelled — which is the drift these lists exist
 * to prevent.
 *
 * Scoped to the cellar, always. That is a privacy property before it is a
 * usability one: a list assembled from everybody's wines would quietly tell
 * each user what the others own.
 */
const suggestion = z.object({
    name: z.string(),
    /** How many wines use it. The picker puts the common ones within reach. */
    uses: z.number().int(),
});
export const suggestionsSchema = z.object({
    /** Carries its standing, so the picker can show what is thought of a
     * house without a second request. */
    producers: z.array(suggestion.extend({ standing: producerStandingSchema })),
    /** Carries its country, so choosing a country narrows the regions offered. */
    regions: z.array(suggestion.extend({ countryCode: z.string() })),
    grapes: z.array(suggestion),
    /**
     * The wine styles this cellar has used. Offered like the grapes and for the
     * same reason: 24 of them across 121 wines, six already carrying a stray
     * tab, and retyping `Södra Rhône Châteauneuf-du-Pape Röda` by hand is how a
     * twenty-fifth appears that is really the third.
     */
    styles: z.array(suggestion),
    /**
     * Only the countries this cellar has already used. The full closed list is
     * the client's business, because it needs Swedish display names that live in
     * the interface bundle — this says which of them belong at the top.
     */
    countries: z.array(suggestion.extend({ countryCode: z.string() })),
    storages: z.array(z.object({ id: z.string(), name: z.string() })),
    /** Carries its storage, so choosing a cabinet narrows the shelves offered. */
    shelves: z.array(z.object({ id: z.string(), name: z.string(), storageId: z.string() })),
    /**
     * Whose judgements this cellar already records — Vivino, Munskänkarna, a
     * magazine, a friend — each with the scale its numbers are out of.
     *
     * The scale travels with the name because the field that records a reading
     * has to say what it is out of before the number is typed, not after. The
     * cellar's own voice is not among them: that is the *Mitt betyg* field, and
     * offering it here would make it possible to file your own opinion twice in
     * two different shapes.
     */
    ratingSources: z.array(suggestion.extend({ scaleMax: z.number(), scaleHasDecimals: z.boolean() })),
    /**
     * Where this cellar has bought wine before — Systembolaget, the producer,
     * Vivino, a restaurant in Piemonte.
     *
     * It offers what the cellar has said and nothing it has not, which is why a
     * cellar that has never bought at auction is not shown *Auktion*: the same
     * rule the producers and regions above follow. Until `9.11` this was five
     * fixed options in a dropdown, and a bottle that came from none of them had
     * to be filed as *Annat*.
     *
     * Defaulted through `9.11`'s release, per the rule in CLAUDE.md: a browser
     * that arrived before the API saw an empty list and a field it could type
     * into, which is exactly what an empty cellar sees anyway. Required as of
     * `10.7` — and this is the field where the default was most comfortable,
     * because its absence and its emptiness look identical on the screen.
     */
    purchaseSources: z.array(suggestion),
    /**
     * The catalogue itself, in the little of each entry needed to recognise a
     * second one — so the form can say *you already have this* before a wine is
     * registered twice.
     *
     * Here rather than behind a search endpoint of its own for the reason the
     * rest of this response exists: the question is asked while the producer,
     * the name and the year are being typed, and a debounced request per
     * keystroke to a container that may be asleep is the wrong shape for it. The
     * comparison is `findDuplicateWines` in `shared/`, over the list the browser
     * already holds, which also keeps the rule in the same module as the
     * normaliser the picker matches with.
     *
     * It is the whole catalogue and not what is in stock, for the same reason
     * the lists above are: a wine drunk to its last bottle keeps its number, and
     * buying it again is precisely the case the duplicate check exists for.
     *
     * 122 wines is a few kilobytes. At a few thousand this becomes a query
     * instead — recorded as a judgement about size rather than an oversight.
     *
     * Defaulted through the release it arrived in: the browser deploys on a
     * green `main` and the API by hand, so a page that parsed this could reach a
     * phone before the API that sent it, and an empty list meant the check
     * simply said nothing. Required as of `10.7`. The quiet failure it would
     * otherwise permit is the expensive one here — a duplicate check that says
     * nothing looks exactly like a duplicate check that found nothing, and the
     * wine gets a second number that is printed on a label the same evening.
     */
    wines: z.array(z.object({
        id: z.string(),
        number: z.number().int(),
        name: z.string(),
        producer: z.string(),
        vintage: z.number().int().nullable(),
        isNonVintage: z.boolean(),
        /**
         * Bottles owned right now, standing on a shelf or out at a dinner —
         * nought for a wine drunk to its last bottle, which is the number the
         * offer most needs to be able to say.
         */
        owned: z.number().int(),
    })),
});
//# sourceMappingURL=suggestions.js.map